import store from '@/store';
import router from '@/routes';
import bus from '@/services/evbus'
import Vue from 'vue';
import { cloneDeep, get } from 'lodash';
import { Route } from 'vue-router';
import { METRICS_CUSTOM_COLUMN, METRICS_CUSTOM_EVENT, METRICS_CUSTOM_EVENT_REVENUE, METRICS_CUSTOM_EVENT_HIDDEN_REVENUE } from '@/services/constants/report';

export type Columns = {[type: string]: number }

export interface ColumnOrder {
  value: string;
  text: string;
}

export interface TypeConfig {
  width: {[type: string]: number };
  order: ColumnOrder[];
  sort: ColumnsSort;
}

export interface UserConfig {
  bookmarks: Bemob.Bookmark[];
  desktopViewForced: boolean
  types?: {[type: string]: TypeConfig }, // obsolete only for import
  queryColumns?: {[type: string]: any }, // obsolete only for import
}

export interface LocalStorageUserData {
  users?: {[type: string]: UserConfig }
}

export const ColumnsLocalStorageKey = 'table-config';

export class ColumnsService extends Vue {
  columnsConfig: Bemob.Metrics = null;

  getColumnWeight(columnName: string): number {
    const option = get(this.columnsConfig, ['metrics_options', columnName]);

    if (option) {
      return option
    }

    if (columnName && (columnName.startsWith(METRICS_CUSTOM_EVENT) ||
      columnName.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE) || columnName.startsWith(METRICS_CUSTOM_EVENT_REVENUE) || columnName.startsWith(METRICS_CUSTOM_COLUMN))) {
      return 100
    }

    return null
  }
  
  install(Vue: any, options: any) { 
    Vue.prototype.$columns = this;
  }

  getDefaultLocalStorageData(): LocalStorageUserData {
    return {
      users: {}
    };
  }

  getDefaultUserData(): UserConfig {
    return {
      bookmarks: [],
      desktopViewForced: store && store.state.mobile.isMobile
        ? false 
        : null
    };
  }

  get type() {
    return this.getType(router.currentRoute);
  }

  getType(currentRoute: Route) {
    switch(currentRoute.name) {
      case 'app.report':
        return currentRoute.params.type;
      
      case 'app.conversions':
        return 'conversions';
      case 'app.postbacks':
        return 'postbacks';

      case 'app.errors':
        return 'errors';

      case 'app.live-clicks':
        return 'live-clicks';

      case 'app.live-visits':
        return 'live-visits';
    }
  }

  get localStorageUserData(): LocalStorageUserData {
    return window.localStorage[ColumnsLocalStorageKey] 
      ? JSON.parse(window.localStorage[ColumnsLocalStorageKey])
      : this.getDefaultLocalStorageData();
  }

  set localStorageUserData(value: LocalStorageUserData) {
    window.localStorage.setItem(ColumnsLocalStorageKey, JSON.stringify(value));
  }

  get currentUserData(): UserConfig {
    const { id } = store.state.user;
    return this.getUserConfig(id);
  }

  fixCurrentUserData() {
    const { id } = store.state.user;
    let data = this.localStorageUserData;

    delete data.users[id].queryColumns;
    delete data.users[id].types;

    this.localStorageUserData = data;
  }

  getUserConfig(id: string) {
    let data = this.localStorageUserData;
    if(!data.users[id]) {
      data.users[id] = this.getDefaultUserData();
      if(id) {
        this.$nextTick(() => {
          this.localStorageUserData = data;   
        });
      }
    }

    return data.users[id];
  }

  setCurrentBookmarks(bookmarks: Bemob.Bookmark[]) {
    let data = this.localStorageUserData;

    const { id } = store.state.user;
  
    if(id) {
      data.users[id].bookmarks = bookmarks;
    }

    this.localStorageUserData = data;
  }

  setCurrentDesktopViewForced(desktopViewForced: boolean) {
    let data = this.localStorageUserData;
    const id = store.state.user?.id;

    if(id && data.users[id]) {
      data.users[id].desktopViewForced = desktopViewForced;
    }

    this.localStorageUserData = data;
  }

  get isReport() {
    return !!router.currentRoute.params.report;
  }

}

export default new ColumnsService();
